"use client";

import { useState } from "react";
import { ModelCard, type TranslatedModelData } from "./ModelCard";
import { ScrollablePricingCardsGrid, type CardItem } from "./ScrollablePricingCardsGrid";

// Card header height (from ModelCard tw-min-h-[12.5rem])
const CARD_HEADER_HEIGHT_REM = 12.5;
const BUTTON_VERTICAL_POSITION_REM = CARD_HEADER_HEIGHT_REM / 2; // Halfway point of card header

interface ModelPricingGridClientProps {
  models: TranslatedModelData[];
  scrollLabels: {
    previousSlide: string;
    nextSlide: string;
  };
  /** ID of the model to highlight by default (defaults to first model) */
  defaultHighlightedId?: string;
}

/**
 * Client component for Model Pricing Grid.
 * Handles scroll navigation for the model cards carousel.
 */
export const ModelPricingGridClient = ({ models, scrollLabels, defaultHighlightedId }: ModelPricingGridClientProps) => {
  // Track which card is highlighted - persists on hover and stays when leaving
  const [highlightedModelId, setHighlightedModelId] = useState<string>(defaultHighlightedId ?? models[0]?.id ?? "");

  const items: CardItem[] = models.map(model => ({
    id: model.id,
    content: (
      <ModelCard
        model={model}
        highlighted={highlightedModelId === model.id}
        onHoverStart={() => setHighlightedModelId(model.id)}
      />
    ),
  }));

  return (
    <ScrollablePricingCardsGrid
      items={items}
      scrollLabels={scrollLabels}
      cardWidthClassName='tw-w-[calc(100%-2rem)] md:tw-w-[calc(50%-1rem)] lg:tw-w-[23%]'
      buttonVerticalPositionRem={BUTTON_VERTICAL_POSITION_REM}
      showBottomBorder={true}
    />
  );
};
